import { AlertTriangle } from 'lucide-react';
import { useBrainStore } from '@/state/brainStore';
import { doubleDayWarningFor, type ProposedSession } from '@/domain/workload';
import { formatStudioDay } from '@/utils/studioDay';

/**
 * Amber, non-blocking notice when a booking would be the artist's second full
 * session on the same day (B9 / 14ym9bv75nk, Anas 1 Oct: a double day is two
 * days' worth of hours in one day; warn only). Renders nothing otherwise.
 */
export default function DoubleDayWarning({
  artistId,
  proposed,
  testId,
}: {
  artistId: string;
  proposed: ProposedSession | null;
  testId: string;
}) {
  const bookings = useBrainStore((s) => s.bookings);
  const artist = useBrainStore((s) => s.artists[artistId]);
  if (!proposed || !artistId) return null;
  const warning = doubleDayWarningFor(artistId, bookings, proposed);
  if (!warning) return null;
  return (
    <p
      role="status"
      data-testid={testId}
      className="mt-3 flex items-start gap-2 rounded-md border border-orange/50 bg-orange/10 px-3 py-2 font-serif text-[12px] text-cream"
    >
      <AlertTriangle size={13} className="mt-0.5 shrink-0 text-orange" />
      <span>
        <span className="font-semibold">Double day.</span> This is {artist?.displayName ?? 'the artist'}&rsquo;s second
        full session on {formatStudioDay(warning.day, 'EEE d MMM')}: two days&rsquo; worth of
        hours in one day. You can still book it; a comp day is arranged separately.
      </span>
    </p>
  );
}
